import "./App.scss";
import Header from "./components/Header/Header";
import { Outlet, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { SideMenu } from "./components/SideMenu/SideMenu";
// import { useEffect, useState, useCallback, useRef } from "react";
import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { LoadingIndicator } from "./components/CommonUIComponents/LoadingIndicator/LoadingIndicator";
import axios from "axios";
import { useGetToken } from "./hooks/useAppToken";
import { setUser, setUserRoles, setAllowedOrderIds } from "./store/slices/userSlice";
import { fetchNotifications } from "./store/slices/notificationsSlice";
import { fetchRegions } from "./store/slices/lookupSlice";
import { AutoLogout } from "./components/AutoLogout/AutoLogout";
import ConsentModal from "./components/ConsentModal/ConsentModal";
import { UserManageemntService } from "./api/services/users.service";
import { OrdersService } from "./api/services";
import { getNotificationRoleSegment } from "./util/notificationRole";

// Sets up user in Redux store and MSAL active account
const UserInterceptorProvider = () => {
  const { accounts, instance } = useMsal();
  const dispatch = useDispatch();
  const account = accounts[0];

  useEffect(() => {
    if (!account) return;
    dispatch(setUser(account));
    instance.setActiveAccount(account);
  }, [account, instance, dispatch]);

  return null;
};

// Fetches notifications when user email is available
const NotificationsFetcher = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user?.user);
  const userRoles = useSelector((state) => state.user?.userRoles);
  const selectedRole = useSelector((state) => state.user?.selectedRole);
  // const userEmail = user?.username || user?.mail;
  const userEmail =
    user?.email ||
    user?.mail ||
    user?.username ||
    user?.preferred_username ||
    user?.upn ||
    "";
  const notificationRole = useMemo(() => {
    if (selectedRole) {
      return selectedRole;
    }

    if (!Array.isArray(userRoles) || userRoles.length === 0) {
      return null;
    }

    return (
      userRoles.find((role) => getNotificationRoleSegment(role) === "DID") ||
      userRoles[0]
    );
  }, [selectedRole, userRoles]);
  const notificationRoleSegment = getNotificationRoleSegment(notificationRole);
  const roleId = notificationRole?.roleId || "";

  useEffect(() => {
   
    if (userEmail && notificationRoleSegment) {
      dispatch(
        fetchNotifications({ email: userEmail, role: notificationRole, roleId }),
      );
    }
  }, [dispatch, notificationRole, notificationRoleSegment, roleId, userEmail]);

  return null;
};

// Fetches regions from lookup API when app loads (only after token is ready)
const RegionsFetcher = ({ token }) => {
  const dispatch = useDispatch();
  const regions = useSelector((state) => state.lookup?.regions);
  const regionsLoading = useSelector((state) => state.lookup?.regionsLoading);
  const regionsLoaded = useSelector((state) => state.lookup?.regionsLoaded);
  const regionsError = useSelector((state) => state.lookup?.regionsError);
  const hasRegions = Array.isArray(regions) && regions.length > 0;

  useEffect(() => {
    // Empty results are valid too; only fetch once until the cache is cleared.
    if (token && !regionsLoading && !regionsLoaded && !hasRegions && !regionsError) {
      dispatch(fetchRegions());
    }
  }, [dispatch, hasRegions, regionsError, regionsLoaded, regionsLoading, token]);

  return null;
};

// Fetches allowed orders when role is set (login) or changed
// Scenarios covered:
// 1. When user logs in and role is set
// 2. When role is changed
const AllowedOrdersFetcher = () => {
  const dispatch = useDispatch();
  const selectedRole = useSelector((state) => state.user?.selectedRole);

  useEffect(() => {
    const fetchAllowedOrders = async () => {
      // Skip if no role selected
      if (!selectedRole) {
        return;
      }

      const roleName = selectedRole?.roleName || selectedRole?.name || '';
      const roleId = selectedRole?.roleId || '';

      // Skip if role is admin (case-insensitive)
      if (roleName.toLowerCase() === 'admin') {
        dispatch(setAllowedOrderIds([]));
        return;
      }

      // Skip if no roleId
      if (!roleId) {
        dispatch(setAllowedOrderIds([]));
        return;
      }

      try {
        const response = await OrdersService.getAIOrders(roleId);

        // Extract orderIds from response and convert to strings
        const rawOrderIds = Array.isArray(response)
          ? response
          : (response?.data?.orderIds || response?.orderIds || []);
        const orderIds = rawOrderIds.map((id) => String(id));
        
        dispatch(setAllowedOrderIds(orderIds));
      } catch (error) {
        console.error('Error fetching AI allowed orders:', error);
        dispatch(setAllowedOrderIds([]));
      }
    };

    fetchAllowedOrders();
  }, [dispatch, selectedRole]);

  return null;
};

// Fetches user roles when user is authenticated and token is ready
const UserRolesFetcher = ({ token }) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user?.user);
  const userRoles = useSelector((state) => state.user?.userRoles);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    // Only fetch if user is authenticated, token is ready, and roles haven't been fetched yet
    const isAuthenticated = user && (user.username || user.mail || user.name);
    const hasToken = token && token.length > 0;


    if (
      isAuthenticated &&
      hasToken &&
      (!userRoles || userRoles.length === 0) &&
      !hasFetchedRef.current
    ) {
      hasFetchedRef.current = true;
      const fetchRoles = async () => {
        try {
          const response = await UserManageemntService.fetchUserRoles(
            "a53f800f-4c24-4974-ae10-6f7da7efb499",
          );
          if (response?.data?.roles) {
            dispatch(setUserRoles(response.data.roles));
          }
        } catch (error) {
          console.error("Error fetching user roles:", error);
          hasFetchedRef.current = false; // Allow retry on error
        }
      };
      fetchRoles();
    }
  }, [dispatch, user, userRoles, token]);

  return null;
};

function App() {
  const [isSideMenuCollapsed, setSideMenuCollapsed] = useState(true);
  const { accounts, inProgress } = useMsal();
  const [hasConsented, setHasConsented] = useState(() => {
    return sessionStorage.getItem("ai_consent_accepted") === "true";
  });
  const token = useGetToken();
  const account = accounts[0];
  const location = useLocation();
  const isAuthenticated = accounts.length > 0;
  const interceptorIdRef = useRef(null);
  // Handles user consent agreement
  const handleConsentAgree = useCallback(() => {
    sessionStorage.setItem("ai_consent_accepted", "true");
    setHasConsented(true);
  }, []);

  // Toggles side menu collapsed state
  const toggleSideMenu = useCallback(() => {
    setSideMenuCollapsed((prev) => !prev);
  }, []);

  // Returns background class based on current route
  const getBackgroundClass = useCallback(() => {
    const path = location.pathname;
    return path === "/" || path === "/home" ? "bg-home" : "bg-others";
  }, [location.pathname]);

  // Set up axios interceptor with cleanup to prevent memory leaks
  useEffect(() => {
    if (!token || !account) return;

    // Remove previous interceptor if exists
    if (interceptorIdRef.current !== null) {
      axios.interceptors.request.eject(interceptorIdRef.current);
    }

    // Add new interceptor and store its ID for cleanup
    interceptorIdRef.current = axios.interceptors.request.use((config) => {
      if (!config.headers["Authorization"]) {
        config.headers["Authorization"] = `Bearer ${token}`;
      }
      config.baseURL = process.env.REACT_APP_API_URL_DEFAULT;
      config.headers["X-Frame-Options"] = "DENY";
      config.headers["Content-Security-Policy"] = "frame-ancestors 'none'";
      config.headers["secure"] = true;
      config.headers["httpOnly"] = true;
      return config;
    });

    // Cleanup on unmount or when dependencies change
    return () => {
      if (interceptorIdRef.current !== null) {
        axios.interceptors.request.eject(interceptorIdRef.current);
        interceptorIdRef.current = null;
      }
    };
  }, [token, account]);

  // Prevent clickjacking by breaking out of iframes
  useEffect(() => {
    if (window.top !== window.self) {
      window.top.location = window.self.location;
    }
  }, []);

  // Show loading while authentication is in progress
  if (inProgress !== InteractionStatus.None && !isAuthenticated) {
    return (
      <div className={`motif-theme-dark main-div ${getBackgroundClass()}`}>
        <LoadingIndicator show={true} fullscreen={true}>
          <div className="app">
            <div className="auth-loading">
              <p>Authenticating...</p>
            </div>
          </div>
        </LoadingIndicator>
      </div>
    );
  }

  // Authenticated user view
  if (isAuthenticated) {
    return (
      <div className={`motif-theme-dark main-div ${getBackgroundClass()}`}>
        {!hasConsented && <ConsentModal onAgree={handleConsentAgree} />}
        <UserInterceptorProvider />
        <NotificationsFetcher />
        <RegionsFetcher token={token} />
        <UserRolesFetcher token={token} />
        <AllowedOrdersFetcher />
        <AutoLogout />
        <LoadingIndicator fullscreen={true}>
          <div className="app">
            <Header setSideMenuCollapsed={toggleSideMenu} />
            <div className="app-body">
              <SideMenu
                isSideMenuCollapsed={isSideMenuCollapsed}
                setSideMenuCollapsed={toggleSideMenu}
              />
              <div
                className={`app-body-content ${
                  isSideMenuCollapsed
                    ? "app-side-menu-collapsed"
                    : "app-side-menu-expanded"
                }`}
              >
                <Outlet />
              </div>
            </div>
          </div>
        </LoadingIndicator>
      </div>
    );
  }

  // Unauthenticated - login redirect handled in index.jsx
  return (
    <div className={`motif-theme-dark main-div ${getBackgroundClass()}`}>
      <div className="app">
        <div className="auth-loading" />
      </div>
    </div>
  );
}

export default App;
